import { useState } from "react"
import { useForm } from "react-hook-form"
import Navbar from "../components/common/Navbar"
import { candidateApi } from "../utils/configuration"


type FormData = {
  firstName: string
  lastName: string
  email: string
  phone?: string
  subject?: string
  message: string
}

const submitContactForm = async (data: FormData): Promise<{ success: boolean; message: string }> => {
  try {
    await candidateApi.candidateContact({
      body: data
    });

    return {
      success: true,
      message: "Your message has been sent successfully!"
    };
  } catch (err) {
    console.error('Error submitting form:', err);
    return {
      success: false,
      message: "Failed to send message. Please try again."
    };
  }
};


export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange", // Validate on change for better UX
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setSubmitError("")

    try {
      const result = await submitContactForm(data)
      
      if (result.success) {
        setSubmitSuccess(true)
        reset() // Reset form fields
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : "There was an error submitting your message. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const faqs = [
    {
      question: "How do I create an account?",
      answer:
        "To create an account, click on the 'Sign Up' button in the top right corner of the page. You'll be asked to provide some basic information and select whether you're a job seeker or recruiter. Follow the prompts to complete your registration.",
    },
    {
      question: "Is it free to use Jobs247?",
      answer:
        "Basic accounts for job seekers are completely free. Recruiters and companies have free trials available, with premium features available through our subscription plans.",
    },
    // {
    //   question: "How can I reset my password?",
    //   answer:
    //     "If you forgot your password, click on 'Sign In', then select the 'Forgot Password' option. Enter your email address, and we'll send you instructions to reset your password.",
    // },
    {
      question: "How do I post a job?",
      answer:
        "Recruiters can post jobs by logging into their account, navigating to the dashboard, and clicking on 'Post a New Job'. Fill out the job details form and submit it for review.",
    },
    {
      question: "What if I need further assistance?",
      answer:
        "If you have any questions or need assistance, you can reach our support team using the contact form on this page, or by emailing connect@jobs247.co.in. We aim to respond to all inquiries within 24 hours during business days.",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700"></div>

        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-white opacity-10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-white opacity-10 rounded-full blur-2xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white opacity-5 rotate-45 rounded-lg"></div>
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-white opacity-5 rotate-12 rounded-lg"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center text-white">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6 shadow-2xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Contact Us</h1>
          <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto leading-relaxed">
            Have questions or need assistance? We're here to help!
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Contact Information */}
              <div className="bg-gradient-to-br from-indigo-700 to-purple-700 text-white p-8 lg:p-12 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 right-10 w-32 h-32 bg-white rounded-full blur-2xl"></div>
                  <div className="absolute bottom-10 left-10 w-24 h-24 bg-white rounded-full blur-xl"></div>
                </div>

                <div className="relative z-10">
                  <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
                  <p className="mb-8 text-indigo-100 text-lg leading-relaxed">
                    Fill out the form and our team will get back to you as soon as possible. We're here to help with
                    any questions you may have.
                  </p>

                  <div className="space-y-8">
                    {/* Phone */}
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mr-4 group-hover:bg-white/30 transition-colors duration-300">
                        <svg className="h-6 w-6 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-indigo-200 mb-2">Phone</h3>
                        <p className="text-lg">+91 91671 90670</p>
                        <p className="text-lg">+91 91671 82345</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mr-4 group-hover:bg-white/30 transition-colors duration-300">
                        <svg className="h-6 w-6 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-indigo-200 mb-2">Email</h3>
                        <p className="text-lg">connect@jobs247.co.in</p>
                      </div>
                    </div>
                  </div>

                  {/* Office Hours */}
                  <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-2xl">
                    <h3 className="font-semibold text-indigo-200 mb-4 text-lg">Our Office Hours</h3>
                    <div className="space-y-2 text-indigo-100">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 10:00 AM - 2:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="mt-12">
                    <h3 className="font-semibold text-indigo-200 mb-4 text-lg">Follow Us</h3>
                    <div className="flex space-x-4">
                      <a
                        href="#"
                        className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
                      >
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                      <a
                        href="#"
                        className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
                      >
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                      </a>
                      <a
                        href="#"
                        className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
                      >
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="p-8 lg:p-12 bg-white">
                <h2 className="text-3xl font-bold mb-6 text-gray-800">Send Us a Message</h2>

                {submitSuccess ? (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-700 p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-xl">Message Sent Successfully!</h3>
                    </div>
                    <p className="mb-4 text-lg">Thank you for reaching out. Our team will get back to you shortly.</p>
                    <button
                      onClick={() => setSubmitSuccess(false)}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {submitError && (
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 text-red-700 p-4 rounded-2xl">
                        {submitError}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name*
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          {...register("firstName", {
                            required: "First name is required",
                            minLength: {
                              value: 2,
                              message: "First name must be at least 2 characters"
                            },
                            pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: "First name can only contain letters and spaces"
                            }
                          })}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 ${
                            errors.firstName
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300 hover:border-indigo-300"
                          }`}
                          placeholder="Enter your first name"
                        />
                        {errors.firstName && (
                          <p className="mt-2 text-sm text-red-600">{errors.firstName.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name*
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          {...register("lastName", {
                            required: "Last name is required",
                            minLength: {
                              value: 2,
                              message: "Last name must be at least 2 characters"
                            },
                            pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: "Last name can only contain letters and spaces"
                            }
                          })}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 ${
                            errors.lastName ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-indigo-300"
                          }`}
                          placeholder="Enter your last name"
                        />
                        {errors.lastName && (
                          <p className="mt-2 text-sm text-red-600">{errors.lastName.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="email"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address"
                          }
                        })}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 ${
                          errors.email ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-indigo-300"
                        }`}
                        placeholder="Enter your email address"
                      />
                      {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        {...register("phone", {
                          pattern: {
                            value: /^[\+]?[1-9][\d]{0,15}$/,
                            message: "Please enter a valid phone number"
                          }
                        })}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 ${
                          errors.phone ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-indigo-300"
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        {...register("subject", {
                          maxLength: {
                            value: 100,
                            message: "Subject must be less than 100 characters"
                          }
                        })}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 ${
                          errors.subject ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-indigo-300"
                        }`}
                        placeholder="Enter the subject"
                      />
                      {errors.subject && <p className="mt-2 text-sm text-red-600">{errors.subject.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Message*
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        {...register("message", {
                          required: "Message is required",
                          minLength: {
                            value: 10,
                            message: "Message must be at least 10 characters long"
                          },
                          maxLength: {
                            value: 1000,
                            message: "Message must be less than 1000 characters"
                          }
                        })}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all duration-200 resize-none ${
                          errors.message ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-indigo-300"
                        }`}
                        placeholder="Enter your message"
                      ></textarea>
                      {errors.message && <p className="mt-2 text-sm text-red-600">{errors.message.message}</p>}
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex justify-center items-center transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                              />
                            </svg>
                            Send Message
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 text-center">Fields marked with * are required</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Find answers to common questions about our platform</p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-gray-50 to-indigo-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-white/50 transition-colors duration-200"
                >
                  <h3 className="text-xl font-bold text-gray-800 pr-4">{faq.question}</h3>
                  <div
                    className={`flex-shrink-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center transition-transform duration-300 ${
                      expandedFaq === index ? "rotate-180" : ""
                    }`}
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    expandedFaq === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-6">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
